import { create } from 'zustand';
import apiClient from '../utils/apiClient';

export const useInputDefinitionsStore = create((set, get) => ({
  definitions: [],
  isLoading: false,
  error: null,
  lastFetched: 0,
  cacheDuration: 60 * 1000,

  fetchDefinitions: async (force = false) => {
    const now = Date.now();
    if (!force && now - get().lastFetched < get().cacheDuration) return;
    set({ isLoading: true, error: null });
    try {
      const list = await apiClient.get('/input-definition-list');
      set({
        definitions: Array.isArray(list) ? list : [],
        isLoading: false,
        lastFetched: now,
        error: null,
      });
    } catch (err) {
      set({ definitions: [], isLoading: false, error: err.message || 'Failed to load definitions' });
    }
  },

  createBlank: async (name = 'Untitled') => {
    const created = await apiClient.post('/input-definition', { name, status: 'draft' });
    set((s) => ({ definitions: [created, ...s.definitions] }));
    return created;
  },

  updateMeta: async (id, patch) => {
    // Use the per-row PUT for full updates (name, status changes, etc.)
    const existing = get().definitions.find((d) => d.id === id);
    const next = { ...existing, ...patch };
    const updated = await apiClient.put(`/input-definition/${id}`, next);
    set((s) => ({ definitions: s.definitions.map((d) => (d.id === id ? updated : d)) }));
    return updated;
  },

  duplicate: async (id) => {
    const res = await apiClient.post('/input-definition-request', { action: 'duplicate', id });
    const created = res?.records?.[0];
    if (created) {
      set((s) => ({ definitions: [created, ...s.definitions] }));
    }
    return created;
  },

  deleteDefinition: async (id) => {
    await apiClient.delete(`/input-definition/${id}`);
    set((s) => ({ definitions: s.definitions.filter((d) => d.id !== id) }));
  },

  importXlsx: async (file) => {
    // Route through the unified upload endpoint — same path that the
    // Reports page uses. Backend auto-creates the matching
    // input_definition row, so the Library reflects it on the next list
    // refresh.
    const data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || "";
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    const res = await apiClient.post("/upload-file-content", {
      filename: file.name,
      data,
    });
    const definitionId = res?.detail?.input_definition_id;
    let created = null;
    if (definitionId) {
      try {
        created = await apiClient.get(`/input-definition/${definitionId}`);
      } catch {
        // The row exists; the Library refresh will pick it up. Don't
        // block the import just because the immediate fetch raced.
      }
    }
    if (created) {
      set((s) => ({ definitions: [created, ...s.definitions] }));
    }
    const warnings = res?.detail?.validation?.findings
      ?.filter?.((f) => f.severity === "warning")
      ?.map?.((f) => f.message) || [];
    return { definition: created, warnings, response: res };
  },

  listVersions: async (id) => {
    const res = await apiClient.post("/input-definition-request", { action: "versions", id });
    return res?.records || [];
  },

  snapshot: async (id, label) => {
    const res = await apiClient.post("/input-definition-request", { action: "snapshot", id, label });
    return res?.metadata?.version_id || null;
  },

  restoreVersion: async (versionId) => {
    const res = await apiClient.post("/input-definition-request", { action: "restore", version_id: versionId });
    const restored = res?.records?.[0];
    if (restored?.id) {
      set((s) => ({ definitions: s.definitions.map((d) => (d.id === restored.id ? restored : d)) }));
    }
    return restored;
  },

  // NB: not named "useInRun" because ESLint's react-hooks/rules-of-hooks
  // misclassifies any identifier starting with "use" as a Hook.
  launchRun: async (id, opts = {}) => {
    const res = await apiClient.post("/input-definition-request", {
      action: "use_in_run",
      id,
      product: opts.product,
      use_postprocessing: !!opts.use_postprocessing,
      use_biointerpreter: !!opts.use_biointerpreter,
      description: opts.description,
      ...(opts.project_id !== undefined ? { project_id: opts.project_id } : {}),
      ...(opts.project_tag ? { project_tag: opts.project_tag } : {}),
    });
    return res?.metadata || {};
  },

  exportXlsx: async (id) => {
    // Returns a Blob via the apiClient.request blob path.
    const result = await apiClient.request(
      'POST',
      '/input-definition-request',
      { action: 'export', id },
      false,
      'blob',
      true
    );
    return result?.data;
  },
}));
